import React from 'react';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { Alert, Box, Divider, MenuItem, Select, Stack, TextField, Toolbar } from '@mui/material';
import { useStyles } from './index.styles';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { getRoutesByProfile } from '@utils/Routes';
import { useDispatch, useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { ProfileTypes } from '@api/Users/User';
import DrinksSold from '@components/SalesReport/DrinksSold';
import { setBranchOfficeSelected, setDistributionCenterSelected, setProfileSelected } from '@store/authStore';
import { getProfileName } from '@utils/Profiles';
import SidebarNav from '@components/Utilities/Sidebar/SidebarNav';
import { SIDEBAR_EXTERNAL_LINKS } from '@navigation/externalLinks';
import SidebarBranchServices from '@components/Utilities/Sidebar/SidebarBranchServices';
import { can, Capability } from '@utils/Permissions/Capabilities';

export const drawerWidth = 400;

interface SidebarProps {
    open: boolean;
    handleDrawerToggle: () => void;
}

// ============================================================================
// SIDEBAR — reestructurado el 2026-08-18 ("el admin ya no se cambia de perfil
// para trabajar", decisión de Irving).
//
// Lo que cambió y POR QUÉ:
//   1. La navegación se movió a `SidebarNav` (grupos colapsables). Aquí solo
//      quedan los selectores de contexto.
//   2. El selector de perfil dejó de ser "cámbiate de perfil para poder hacer
//      cosas" y pasó a ser **"Ver como"**: una PREVISUALIZACIÓN. El admin ya
//      tiene todas las pantallas en su propio perfil.
//      ⚠️ `hasPermission` lee `profileSelected`, así que mientras previsualiza
//      el admin OPERA con los permisos de ese perfil (decisión de Irving: sirve
//      para reproducir lo que reporta el staff). Por eso el banner de aviso.
//   3. Los toggles de Pickup/Delivery y la configuración de envío se ELIMINARON
//      de aquí: eran un duplicado del formulario de Sucursales, que ya tiene
//      esos campos con validación. El gerente los edita en "Mi sucursal".
// ============================================================================

const Sidebar: React.FunctionComponent<SidebarProps> = (props) => {
    const { open, handleDrawerToggle } = props;
    const { loggedUser, profileSelected, branchOfficeSelected, distributionCenterSelected } = useSelector(
        (state: RootReducer) => state.auth
    );
    const { branchOffices, distributionCenters } = useSelector((state: RootReducer) => state.krispis);
    const dispatch = useDispatch();
    const classes = useStyles();

    const isAdmin = loggedUser?.profile.profile === ProfileTypes.Admin;
    // Previsualizando: soy admin pero estoy mirando el sistema como otro perfil
    const isPreviewing = isAdmin && profileSelected !== ProfileTypes.Admin;

    // Gerente y admin de sucursal eligen entre las sucursales que tengan asignadas;
    // el admin, entre todas. Un usuario de CEDIS no elige: su centro se resuelve por
    // su uid. Se pregunta por el perfil REAL: "Ver como" no le quita el selector al admin.
    const showBranchSelector = can({ profile: loggedUser?.profile.profile }, Capability.BranchSelector);
    const showCedisSelector = isAdmin;

    return (
        <Drawer
            anchor="left"
            open={open}
            onClose={handleDrawerToggle}
            classes={{
                paper: classes.drawerPaper
            }}
        >
            <Toolbar>
                <Stack flex={1} direction="row" justifyContent="space-between">
                    <Stack flex={1} direction="row" spacing={2} alignItems="center">
                        <IconButton color="primary" onClick={handleDrawerToggle}>
                            <CloseIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight="600">
                            Menu
                        </Typography>
                    </Stack>
                    <Stack flex={1} direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
                        <Typography variant="caption" color="darkgray">
                            {`v${process.env.REACT_APP_VERSION}`}
                        </Typography>
                    </Stack>
                </Stack>
            </Toolbar>

            {/* Contexto: sucursal / CEDIS sobre el que se trabaja */}
            <Stack p={2} spacing={2}>
                {showBranchSelector && (
                    <TextField
                        select
                        size="small"
                        label="Sucursal"
                        value={branchOfficeSelected?.id || 'non'}
                        onChange={(e) => {
                            dispatch(setBranchOfficeSelected(branchOffices.find((b) => b.id === e.target.value)));
                        }}
                    >
                        <MenuItem value="non">Selecciona una sucursal</MenuItem>
                        {branchOffices.map((branchOffice, index) => (
                            <MenuItem key={index} value={branchOffice.id}>
                                {branchOffice.name}
                            </MenuItem>
                        ))}
                    </TextField>
                )}

                {showCedisSelector && (
                    <TextField
                        select
                        size="small"
                        label="Centro de distribución"
                        value={distributionCenterSelected?.id || 'non'}
                        onChange={(e) => {
                            dispatch(
                                setDistributionCenterSelected(
                                    distributionCenters.find((d) => d.id === e.target.value)
                                )
                            );
                        }}
                    >
                        <MenuItem value="non">Selecciona un cedis</MenuItem>
                        {distributionCenters.map((distributionCenter, index) => (
                            <MenuItem key={index} value={distributionCenter.id}>
                                {distributionCenter.name}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
            </Stack>

            <Divider />

            <SidebarNav
                routes={getRoutesByProfile(profileSelected)}
                profile={profileSelected}
                links={SIDEBAR_EXTERNAL_LINKS}
                onNavigate={handleDrawerToggle}
            />

            {profileSelected === ProfileTypes.Collaborator && <DrinksSold />}

            {/* Atajo de operación: abrir/cerrar Pickup y Delivery sin salir del
                menú. Lo ven gerente, admin de sucursal y admin (el admin ya no
                cambia de perfil para trabajar); el colaborador no configura servicios. */}
            {can({ profile: profileSelected }, Capability.BranchServicesToggle) && <SidebarBranchServices />}

            {/* "Ver como": PREVISUALIZAR el sistema con los ojos de otro perfil.
                Va hasta abajo a propósito — es una herramienta de diagnóstico,
                no algo que se use para trabajar. */}
            {isAdmin && (
                <Box p={2}>
                    <Divider sx={{ mb: 2 }} />
                    <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <VisibilityIcon fontSize="small" color="disabled" />
                            <Typography variant="overline" fontWeight={700} color="text.secondary">
                                Ver como
                            </Typography>
                        </Stack>
                        <Select
                            id="profile-select"
                            size="small"
                            value={profileSelected}
                            onChange={(e) => {
                                dispatch(setProfileSelected(e.target.value as ProfileTypes));
                                handleDrawerToggle();
                            }}
                        >
                            {Object.values(ProfileTypes).map((profile, index) => (
                                <MenuItem key={index} value={profile}>
                                    {profile === ProfileTypes.Admin
                                        ? 'Yo (Admin) — todas las pantallas'
                                        : getProfileName(profile)}
                                </MenuItem>
                            ))}
                        </Select>
                        {isPreviewing ? (
                            <Alert severity="warning" sx={{ py: 0 }}>
                                Estás operando con los permisos de {getProfileName(profileSelected)}.
                            </Alert>
                        ) : (
                            <Typography variant="caption" color="text.secondary">
                                Solo para revisar cómo se ve el sistema desde otro perfil. No necesitas cambiarte para
                                usar ninguna pantalla.
                            </Typography>
                        )}
                    </Stack>
                </Box>
            )}
        </Drawer>
    );
};

export default Sidebar;
